import React, { useState, useEffect } from 'react';
import { staffService as authService, posService } from '../services/dataService';
import { supabase } from '../services/supabase';
import { isOnline } from '../services/syncService';

const isDesktop = !!window.menuronDesktop?.isDesktop;

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

async function ownerApi(action, payload) {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token || SUPABASE_ANON_KEY;
  const res = await fetch(`${SUPABASE_URL}/functions/v1/owner-api`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ action, ...payload }),
  });
  const json = await res.json();
  if (!res.ok) throw new Error(json.error || 'Request failed');
  return json;
}

const inputStyle = {
  width: '100%', padding: '12px 15px',
  border: '2px solid #e5e7eb', borderRadius: '10px',
  fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.2s',
};

const btn = (variant = 'primary', disabled = false) => {
  const base = {
    border: 'none', borderRadius: '10px', padding: '10px 20px',
    fontSize: '0.9rem', fontWeight: '700',
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.2s',
  };
  if (disabled) return { ...base, background: '#e5e7eb', color: '#9ca3af' };
  if (variant === 'primary') return { ...base, background: 'linear-gradient(135deg, #FF4444, #FF6B00)', color: 'white', boxShadow: '0 3px 10px rgba(255,68,68,0.25)' };
  if (variant === 'outline') return { ...base, background: 'transparent', color: '#666', border: '2px solid #e5e7eb' };
  if (variant === 'danger') return { ...base, background: '#dc2626', color: 'white' };
  if (variant === 'green') return { ...base, background: '#16a34a', color: 'white' };
  return base;
};

// ─── Add Manager Modal ────────────────────────────────────────────────────────
const AddManagerModal = ({ restaurant, onSave, onClose }) => {
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Name is required.';
    if (!form.email.trim()) e.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (!form.password) e.password = 'Password is required.';
    else if (form.password.length < 6) e.password = 'Minimum 6 characters.';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const result = await ownerApi('createManager', {
        restaurantId: restaurant.id,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });
      onSave(result?.data);
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>
      <div style={{
        background: 'white', borderRadius: '20px', padding: '32px',
        width: '100%', maxWidth: '460px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}>
        <h3 style={{ margin: '0 0 24px 0', fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
          Add Manager
        </h3>

        {errors.general && (
          <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem' }}>
            {errors.general}
          </div>
        )}

        {[
          { key: 'name', label: 'Full Name', placeholder: 'Manager name', type: 'text' },
          { key: 'email', label: 'Email Address', placeholder: 'manager@example.com', type: 'email' },
        ].map(({ key, label, placeholder, type }) => (
          <div key={key} style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>{label}</label>
            <input
              type={type}
              placeholder={placeholder}
              value={form[key]}
              onChange={e => set(key, e.target.value)}
              style={{ ...inputStyle, borderColor: errors[key] ? '#dc2626' : '#e5e7eb' }}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = errors[key] ? '#dc2626' : '#e5e7eb'}
            />
            {errors[key] && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0' }}>{errors[key]}</p>}
          </div>
        ))}

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>Password</label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Min. 6 characters"
              value={form.password}
              onChange={e => set('password', e.target.value)}
              style={{ ...inputStyle, borderColor: errors.password ? '#dc2626' : '#e5e7eb', paddingRight: '44px' }}
              onFocus={e => e.target.style.borderColor = '#FF6B00'}
              onBlur={e => e.target.style.borderColor = errors.password ? '#dc2626' : '#e5e7eb'}
            />
            <button type="button" onClick={() => setShowPassword(v => !v)}
              style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
              {showPassword
                ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              }
            </button>
          </div>
          {errors.password && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0' }}>{errors.password}</p>}
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>Confirm Password</label>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder="Re-enter password"
            value={form.confirmPassword}
            onChange={e => set('confirmPassword', e.target.value)}
            style={{ ...inputStyle, borderColor: errors.confirmPassword ? '#dc2626' : '#e5e7eb' }}
            onFocus={e => e.target.style.borderColor = '#FF6B00'}
            onBlur={e => e.target.style.borderColor = errors.confirmPassword ? '#dc2626' : '#e5e7eb'}
          />
          {errors.confirmPassword && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0' }}>{errors.confirmPassword}</p>}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleSave} disabled={saving} style={{ ...btn('primary', saving), flex: 1, padding: '13px' }}>
            {saving ? 'Creating...' : 'Create Manager'}
          </button>
          <button onClick={onClose} style={{ ...btn('outline'), padding: '13px 20px' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Reset Password Modal ─────────────────────────────────────────────────────
const ResetPasswordModal = ({ manager, onClose }) => {
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const set = (k, v) => {
    setForm(prev => ({ ...prev, [k]: v }));
    setErrors(prev => ({ ...prev, [k]: '' }));
  };

  const handleReset = async () => {
    const e = {};
    if (!form.password) e.password = 'Password is required.';
    else if (form.password.length < 6) e.password = 'Minimum 6 characters.';
    if (form.password !== form.confirmPassword) e.confirmPassword = 'Passwords do not match.';
    if (Object.keys(e).length) { setErrors(e); return; }

    setSaving(true);
    try {
      await ownerApi('resetManagerPassword', {
        managerId: manager.id,
        newPassword: form.password,
      });
      setSuccess(true);
    } catch (err) {
      setErrors({ general: err.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>
      <div style={{
        background: 'white', borderRadius: '20px', padding: '32px',
        width: '100%', maxWidth: '420px',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.3rem', fontWeight: '700', color: '#333' }}>
          Reset Password
        </h3>
        <p style={{ margin: '0 0 24px 0', color: '#666', fontSize: '0.9rem' }}>
          Setting a new password for <strong>{manager.name}</strong>. Share it with them directly.
        </p>

        {success ? (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
            <p style={{ color: '#16a34a', fontWeight: '700', fontSize: '1.1rem', margin: '0 0 20px' }}>
              Password reset successfully!
            </p>
            <button onClick={onClose} style={{ ...btn('primary'), width: '100%', padding: '13px' }}>
              Done
            </button>
          </div>
        ) : (
          <>
            {errors.general && (
              <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem' }}>
                {errors.general}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>New Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min. 6 characters"
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  style={{ ...inputStyle, borderColor: errors.password ? '#dc2626' : '#e5e7eb', paddingRight: '44px' }}
                  onFocus={e => e.target.style.borderColor = '#FF6B00'}
                  onBlur={e => e.target.style.borderColor = errors.password ? '#dc2626' : '#e5e7eb'}
                />
                <button type="button" onClick={() => setShowPassword(v => !v)}
                  style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0 }}>
                  {showPassword
                    ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                  }
                </button>
              </div>
              {errors.password && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0' }}>{errors.password}</p>}
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '700', color: '#555', marginBottom: '5px' }}>Confirm New Password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter password"
                value={form.confirmPassword}
                onChange={e => set('confirmPassword', e.target.value)}
                style={{ ...inputStyle, borderColor: errors.confirmPassword ? '#dc2626' : '#e5e7eb' }}
                onFocus={e => e.target.style.borderColor = '#FF6B00'}
                onBlur={e => e.target.style.borderColor = errors.confirmPassword ? '#dc2626' : '#e5e7eb'}
              />
              {errors.confirmPassword && <p style={{ color: '#dc2626', fontSize: '0.8rem', margin: '4px 0 0' }}>{errors.confirmPassword}</p>}
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={handleReset} disabled={saving} style={{ ...btn('primary', saving), flex: 1, padding: '13px' }}>
                {saving ? 'Resetting...' : 'Reset Password'}
              </button>
              <button onClick={onClose} style={{ ...btn('outline'), padding: '13px 20px' }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Manager Analytics ────────────────────────────────────────────────────────
const QUICK_RANGES = [
  { label: 'Today', days: 0 },
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'This Month', days: -1 },
];

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ManagerAnalytics = ({ restaurant, managers }) => {
  const today = new Date();
  const iso = (d) => d.toISOString().split('T')[0];

  const [quickIdx, setQuickIdx] = useState(1); // default: last 7 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7); return iso(d);
  });
  const [endDate, setEndDate] = useState(iso(today));
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  const applyQuick = (idx) => {
    setQuickIdx(idx);
    const { days } = QUICK_RANGES[idx];
    const end = new Date();
    let start = new Date();
    if (days === -1) {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else {
      start.setDate(start.getDate() - days);
    }
    setStartDate(iso(start));
    setEndDate(iso(end));
  };

  useEffect(() => { loadAnalytics(); }, [startDate, endDate]);

  const loadAnalytics = async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    setError('');
    let data;
    try {
      const json = await ownerApi('getManagerAnalytics', { restaurantId: restaurant.id, startDate, endDate });
      data = json.data;
    } catch (err) {
      setError('Failed to load analytics.');
      setLoading(false);
      return;
    }

    // Group by manager (store ID only; names resolved at render time)
    const byManager = {};
    (data || []).forEach(order => {
      if (order.is_invalid) return;
      const id = order.billed_by_manager_id || '__none__';
      if (!byManager[id]) byManager[id] = { id, invoices: 0, revenue: 0, discount: 0, cash: 0, card: 0, upi: 0, other: 0 };
      const r = byManager[id];
      r.invoices += 1;
      r.revenue += Number(order.total_payable || 0);
      r.discount += Number(order.discount_amount || 0);
      const pm = (order.payment_method || '').toLowerCase();
      if (pm === 'cash') r.cash += 1;
      else if (pm === 'card') r.card += 1;
      else if (pm === 'upi') r.upi += 1;
      else r.other += 1;
    });

    const rows = Object.values(byManager).sort((a, b) => b.revenue - a.revenue);
    setStats(rows);
    setLoading(false);
  };

  const totals = stats ? stats.reduce((acc, r) => ({
    invoices: acc.invoices + r.invoices,
    revenue: acc.revenue + r.revenue,
    discount: acc.discount + r.discount,
  }), { invoices: 0, revenue: 0, discount: 0 }) : null;

  const getDisplayName = (id) => {
    if (id === '__none__') return 'Owner';
    return managers.find(m => m.id === id)?.name || 'Unknown Manager';
  };

  const cellStyle = { padding: '14px 16px', borderBottom: '1px solid #f3f4f6', fontSize: '0.9rem', color: '#333' };
  const headStyle = { padding: '10px 16px', background: '#f9fafb', fontWeight: '700', fontSize: '0.8rem', color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em' };

  return (
    <div>
      {/* Date Range Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '20px' }}>
        {QUICK_RANGES.map((r, i) => (
          <button
            key={i}
            onClick={() => applyQuick(i)}
            style={{
              padding: '7px 16px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: '600',
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: quickIdx === i ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
              color: quickIdx === i ? 'white' : '#555',
            }}
          >
            {r.label}
          </button>
        ))}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '4px' }}>
          <input
            type="date"
            value={startDate}
            max={endDate}
            onChange={e => { setStartDate(e.target.value); setQuickIdx(-1); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '0.85rem', outline: 'none' }}
          />
          <span style={{ color: '#999', fontSize: '0.85rem' }}>to</span>
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={iso(today)}
            onChange={e => { setEndDate(e.target.value); setQuickIdx(-1); }}
            style={{ padding: '7px 10px', borderRadius: '8px', border: '2px solid #e5e7eb', fontSize: '0.85rem', outline: 'none' }}
          />
        </div>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '16px', color: '#dc2626', fontSize: '0.9rem' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
          <div style={{ width: '40px', height: '40px', border: '3px solid #FF4444', borderTop: '3px solid transparent', borderRadius: '50%', margin: '0 auto 16px', animation: 'spin 1s linear infinite' }} />
          Loading analytics...
        </div>
      ) : !stats || stats.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', color: '#666' }}>
          No invoices found for the selected period.
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
              <thead>
                <tr>
                  {['Manager', 'Invoices', 'Revenue', 'Avg Bill', 'Discount Given', 'Payment Methods'].map(h => (
                    <th key={h} style={{ ...headStyle, textAlign: h === 'Manager' ? 'left' : 'right', ...(h === 'Payment Methods' ? { textAlign: 'left' } : {}) }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {stats.map((row, i) => {
                  const avg = row.invoices ? row.revenue / row.invoices : 0;
                  const isTop = i === 0 && stats.length > 1;
                  return (
                    <tr key={row.id} style={{ background: isTop ? '#fff7ed' : 'white' }}>
                      <td style={{ ...cellStyle, fontWeight: '600' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '34px', height: '34px', borderRadius: '50%', flexShrink: 0,
                            background: isTop ? 'linear-gradient(135deg, #FF4444, #FF6B00)' : '#f3f4f6',
                            color: isTop ? 'white' : '#555',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: '700', fontSize: '0.95rem',
                          }}>
                            {getDisplayName(row.id).charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div>{getDisplayName(row.id)}</div>
                            {isTop && <div style={{ fontSize: '0.72rem', color: '#f97316', fontWeight: '700' }}>Top Performer</div>}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '600' }}>{row.invoices}</td>
                      <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700', color: '#16a34a' }}>{fmt(row.revenue)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right' }}>{fmt(avg)}</td>
                      <td style={{ ...cellStyle, textAlign: 'right', color: row.discount > 0 ? '#dc2626' : '#999' }}>
                        {row.discount > 0 ? fmt(row.discount) : '—'}
                      </td>
                      <td style={{ ...cellStyle }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          {[['Cash', row.cash, '#16a34a'], ['Card', row.card, '#2563eb'], ['UPI', row.upi, '#7c3aed'], ['Other', row.other, '#6b7280']].filter(([, n]) => n > 0).map(([label, count, color]) => (
                            <span key={label} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '700', background: color + '15', color }}>
                              {label} {count}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f9fafb' }}>
                  <td style={{ ...cellStyle, fontWeight: '700', color: '#333' }}>Total</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700' }}>{totals.invoices}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700', color: '#16a34a' }}>{fmt(totals.revenue)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700' }}>{totals.invoices ? fmt(totals.revenue / totals.invoices) : '—'}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: '700', color: '#dc2626' }}>{fmt(totals.discount)}</td>
                  <td style={{ ...cellStyle }} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ManagerManagement = ({ restaurant, owner }) => {
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [pageError, setPageError] = useState('');
  const [activeTab, setActiveTab] = useState('manage'); // 'manage' | 'analytics'

  useEffect(() => {
    if (restaurant?.id) loadManagers();
  }, [restaurant?.id]);

  const loadManagers = async () => {
    setLoading(true);
    try {
      const { data, error } = await authService.getRestaurantManagers(restaurant.id);
      if (error) setPageError('Failed to load managers.');
      setManagers(data || []);
    } catch {
      setPageError('Failed to load managers.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (manager) => {
    setTogglingId(manager.id);
    const { error } = await authService.updateManagerStatus(manager.id, !manager.is_active);
    if (error) setPageError('Failed to update manager status.');
    else {
      const updated = { ...manager, is_active: !manager.is_active };
      setManagers(prev => prev.map(m => m.id === manager.id ? updated : m));
      window.menuronDesktop?.local?.upsertManager(updated);
    }
    setTogglingId(null);
  };

  const handleDelete = async (manager) => {
    if (!window.confirm(`Permanently delete ${manager.name}? This cannot be undone.`)) return;
    setTogglingId(manager.id);
    const { error } = await authService.deleteManager(manager.id);
    if (error) setPageError('Failed to delete manager.');
    else {
      setManagers(prev => prev.filter(m => m.id !== manager.id));
      window.menuronDesktop?.local?.removeManager(manager.id);
    }
    setTogglingId(null);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ background: 'white', padding: '40px', borderRadius: '20px', textAlign: 'center', boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          <div style={{ width: '48px', height: '48px', border: '4px solid #FF4444', borderTop: '4px solid transparent', borderRadius: '50%', margin: '0 auto 20px', animation: 'spin 1s linear infinite' }} />
          <h2 style={{ color: '#333', margin: 0 }}>Loading Managers...</h2>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '30px 20px' }}>

      {/* Offline banner */}
      {isDesktop && !isOnline() && (
        <div style={{
          background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '10px',
          padding: '12px 16px', marginBottom: '20px',
          display: 'flex', alignItems: 'center', gap: '10px',
          color: '#92400e', fontSize: '0.9rem',
        }}>
          <span style={{ fontSize: '1.1rem' }}>⚠️</span>
          <span><strong>You're offline.</strong> Showing cached staff list. Adding or modifying managers requires an internet connection.</span>
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ margin: '0 0 4px 0', fontSize: '1.8rem', fontWeight: '700', background: 'linear-gradient(135deg, #FF4444, #FF6B00)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Manager Management
          </h1>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            Add and manage staff who can access POS, billing and operational features.
          </p>
        </div>
        {activeTab === 'manage' && (
          isDesktop && !isOnline() ? (
            <div title="Internet connection required to add managers" style={{
              background: '#e5e7eb', color: '#9ca3af',
              border: 'none', padding: '12px 22px', borderRadius: '10px',
              fontSize: '0.9rem', fontWeight: '700', cursor: 'not-allowed',
            }}>
              + Add Manager
            </div>
          ) : (
            <button onClick={() => setShowAddModal(true)} style={{ ...btn('primary'), padding: '12px 22px' }}>
              + Add Manager
            </button>
          )
        )}
      </div>

      {/* Tab toggle */}
      <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', borderRadius: '12px', padding: '4px', marginBottom: '24px', width: 'fit-content' }}>
        {[['manage', 'Manage Managers'], ['analytics', 'Analytics']].map(([id, label]) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            style={{
              padding: '8px 20px', borderRadius: '9px', border: 'none', cursor: 'pointer',
              fontSize: '0.9rem', fontWeight: '700', transition: 'all 0.15s',
              background: activeTab === id ? 'white' : 'transparent',
              color: activeTab === id ? '#333' : '#888',
              boxShadow: activeTab === id ? '0 1px 6px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {pageError && (
        <div style={{ background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '10px', padding: '14px', marginBottom: '20px', color: '#dc2626', fontSize: '0.9rem' }}>
          {pageError}
        </div>
      )}

      {activeTab === 'analytics' && (
        <ManagerAnalytics restaurant={restaurant} managers={managers} />
      )}

      {/* Manager list */}
      {activeTab === 'manage' && (managers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', background: 'white', borderRadius: '20px', boxShadow: '0 4px 20px rgba(0,0,0,0.07)' }}>
          <h3 style={{ color: '#333', margin: '0 0 8px', fontSize: '1.3rem', fontWeight: '600' }}>No managers yet</h3>
          <p style={{ color: '#666', margin: '0 0 24px' }}>Add managers to give them access to POS and operational features.</p>
          {isDesktop && !isOnline() ? (
            <div style={{ color: '#9ca3af', fontSize: '0.9rem', fontStyle: 'italic' }}>
              Connect to the internet to add managers.
            </div>
          ) : (
            <button onClick={() => setShowAddModal(true)} style={{ ...btn('primary'), padding: '12px 28px' }}>
              Add First Manager
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {managers.map(manager => (
            <div
              key={manager.id}
              style={{
                background: 'white', borderRadius: '14px', padding: '20px 24px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: `1px solid ${manager.is_active ? 'rgba(0,0,0,0.06)' : '#fecaca'}`,
                opacity: manager.is_active ? 1 : 0.75,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexWrap: 'wrap', gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{
                  width: '44px', height: '44px', borderRadius: '50%',
                  background: manager.is_active
                    ? 'linear-gradient(135deg, #FF4444, #FF6B00)'
                    : '#e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: 'white', fontWeight: '700', fontSize: '1.1rem', flexShrink: 0,
                }}>
                  {manager.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '700', fontSize: '1rem', color: '#333' }}>
                    {manager.name}
                    {!manager.is_active && (
                      <span style={{ marginLeft: '8px', background: '#fee2e2', color: '#dc2626', fontSize: '0.72rem', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>
                        Inactive
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#888', marginTop: '2px' }}>{manager.email}</div>
                  <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '2px' }}>
                    Added {new Date(manager.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setResetTarget(manager)}
                  style={{ ...btn('outline'), padding: '8px 16px', fontSize: '0.85rem' }}
                >
                  Reset Password
                </button>
                <button
                  onClick={() => handleToggleStatus(manager)}
                  disabled={togglingId === manager.id}
                  style={{
                    ...btn(manager.is_active ? 'danger' : 'green', togglingId === manager.id),
                    padding: '8px 16px', fontSize: '0.85rem',
                  }}
                >
                  {togglingId === manager.id
                    ? 'Updating...'
                    : manager.is_active ? 'Deactivate' : 'Reactivate'
                  }
                </button>
                {!manager.is_active && (
                  <button
                    onClick={() => handleDelete(manager)}
                    disabled={togglingId === manager.id}
                    style={{ ...btn('outline', togglingId === manager.id), padding: '8px 16px', fontSize: '0.85rem', color: '#dc2626', borderColor: '#fca5a5' }}
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ))}

      {showAddModal && (
        <AddManagerModal
          restaurant={restaurant}
          onSave={(manager) => {
            setShowAddModal(false);
            if (manager) {
              setManagers(prev => [manager, ...prev]);
              window.menuronDesktop?.local?.upsertManager(manager);
            }
          }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {resetTarget && (
        <ResetPasswordModal
          manager={resetTarget}
          onClose={() => setResetTarget(null)}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ManagerManagement;
